import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FRAME_IMAGE } from './constants';

// Scandinavian Wooden Frame with elegant reveal animation
export const RealWoodenFrame = ({ src, alt, className = "", isLoaded = false }) => {
  return (
    <div className={`relative ${className}`}>
      <div 
        className="relative p-6 md:p-10"
        style={{ backgroundImage: `url(${FRAME_IMAGE})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }}
      >
        <div className="relative overflow-hidden bg-[#2F2F2F]" style={{ aspectRatio: '4/3' }}>
          <motion.div
            className="w-full h-full relative overflow-hidden"
            initial={{ clipPath: 'inset(50% 50% 50% 50%)' }}
            animate={isLoaded ? { clipPath: 'inset(0% 0% 0% 0%)' } : {}}
            transition={{ duration: 1.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.img 
              src={src} alt={alt} className="w-full h-full object-cover"
              initial={{ scale: 1.3, filter: 'blur(20px) brightness(0.3)' }}
              animate={isLoaded ? { scale: 1, filter: 'blur(0px) brightness(1)' } : {}}
              transition={{ duration: 2.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(120deg, transparent 30%, rgba(201,122,43,0.35) 50%, transparent 70%)' }}
              initial={{ x: '-200%' }}
              animate={isLoaded ? { x: '300%' } : { x: '-200%' }}
              transition={{ duration: 2.2, delay: 1.5, ease: 'linear' }}
            />
          </motion.div>
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 30px rgba(0,0,0,0.15)' }} />
        </div>
      </div>
    </div>
  );
};

// Wrapper with IntersectionObserver for scroll-triggered animation
export const RealWoodenFrameWithTrigger = ({ src, alt, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      <RealWoodenFrame src={src} alt={alt} isLoaded={isVisible} />
    </div>
  );
};

// Backwards compatibility alias
export const AuthenticWoodenFrame = RealWoodenFrameWithTrigger;

// AUTHENTIC NEPALESE WOODEN WINDOW FRAME (Jhyaal) with door opening animation
export const NepaleseWindowFrame = ({ src, alt, isLoaded, className = "", aspectRatio = "3/4", size = "large" }) => {
  const sizes = {
    small: { padding: 'p-2', roof: 'h-6 md:h-8', pillar: 'w-4 md:w-6' },
    medium: { padding: 'p-2 md:p-3', roof: 'h-8 md:h-12', pillar: 'w-5 md:w-8' },
    large: { padding: 'p-3 md:p-4', roof: 'h-10 md:h-14', pillar: 'w-6 md:w-10' }
  };
  const s = sizes[size] || sizes.large;
  
  return (
    <div className={`relative ${className}`}>
      <div className="relative" style={{ perspective: '1500px' }}>
        {/* Ornate Roof */}
        <div className="relative mx-4 md:mx-6">
          <div className="absolute -top-6 md:-top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
            <div className="w-4 h-4 md:w-6 md:h-6 rounded-full mb-1" style={{ background: 'linear-gradient(135deg, #8B4513 0%, #5D3A1A 50%, #8B4513 100%)' }} />
            <div className="w-16 md:w-24 h-3 md:h-4" style={{ background: 'linear-gradient(180deg, #6B3A1F 0%, #4A2512 100%)', clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }} />
          </div>
          <div className={`relative ${s.roof} w-full rounded-t-lg overflow-hidden`} style={{ background: 'linear-gradient(180deg, #6B3A1F 0%, #4A2512 30%, #3D1F0F 100%)', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>
            <div className="absolute bottom-0 left-0 right-0 h-3 md:h-4 flex justify-center">
              {[...Array(15)].map((_, i) => (
                <div key={i} className="w-3 md:w-4 h-full" style={{ background: 'linear-gradient(180deg, #5D3A1A 0%, #4A2512 100%)', borderRadius: '0 0 50% 50%', marginLeft: i > 0 ? '-2px' : '0' }} />
              ))}
            </div>
            <div className="absolute top-1 left-4 right-4 h-2 border-b-2 border-[#8B5A2B]/50" style={{ borderBottomStyle: 'wavy' }} />
          </div>
          <div className="relative h-4 md:h-6 -mt-1" style={{ background: 'linear-gradient(180deg, #4A2512 0%, #3D1F0F 100%)' }}>
            <div className="absolute bottom-0 left-2 right-2 h-2 flex justify-around">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="w-2 h-full bg-[#2D1508]" style={{ clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Main Frame with Pillars */}
        <div className="relative flex">
          {/* Left pillar */}
          <div className={`relative ${s.pillar} flex-shrink-0`} style={{ background: 'linear-gradient(90deg, #3D1F0F 0%, #5D3A1A 30%, #6B3A1F 50%, #5D3A1A 70%, #3D1F0F 100%)', boxShadow: '-3px 0 10px rgba(0,0,0,0.4)' }}>
            <div className="absolute inset-1 flex flex-col justify-around items-center py-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="relative">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded-full border border-[#8B5A2B]" />
                  <div className="absolute inset-0 flex items-center justify-center"><div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8B5A2B]/40" /></div>
                </div>
              ))}
            </div>
            <div className="absolute -bottom-2 -right-2 w-6 h-8 md:w-8 md:h-12" style={{ background: 'linear-gradient(135deg, #5D3A1A 0%, #3D1F0F 100%)', borderRadius: '0 0 100% 0', clipPath: 'ellipse(100% 100% at 0% 0%)' }} />
          </div>

          {/* Center window */}
          <div className={`flex-1 ${s.padding} relative`} style={{ background: 'linear-gradient(180deg, #3D1F0F 0%, #2D1508 50%, #1E0F05 100%)' }}>
            <div className="relative p-1 md:p-2 rounded-sm" style={{ background: 'linear-gradient(135deg, #4A2512 0%, #3D1F0F 50%, #4A2512 100%)', boxShadow: 'inset 0 0 15px rgba(0,0,0,0.5)' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-4 md:h-6" style={{ background: 'transparent', borderBottom: '2px solid #5D3A1A', borderRadius: '0 0 50% 50%' }} />
              <div className="relative overflow-hidden bg-[#2F2F2F]" style={{ aspectRatio, transformStyle: 'preserve-3d' }}>
                {/* Wooden Doors */}
                <motion.div className="absolute inset-0 z-30 flex pointer-events-none" style={{ transformStyle: 'preserve-3d' }}>
                  {/* Left door */}
                  <motion.div
                    className="w-1/2 h-full origin-left relative"
                    style={{ background: 'linear-gradient(90deg, #3D1F0F 0%, #5D3A1A 20%, #6B3A1F 50%, #5D3A1A 80%, #3D1F0F 100%)', boxShadow: 'inset -3px 0 8px rgba(0,0,0,0.4), 4px 0 12px rgba(0,0,0,0.3)', borderRight: '3px solid #2D1508', transformStyle: 'preserve-3d' }}
                    initial={{ rotateY: 0 }}
                    animate={isLoaded ? { rotateY: -105 } : { rotateY: 0 }}
                    transition={{ duration: 2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="absolute inset-2 md:inset-3 border-2 border-[#4A2512]">
                      <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full p-1">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="relative border border-[#5D3A1A] overflow-hidden">
                            <svg className="w-full h-full" viewBox="0 0 30 40" preserveAspectRatio="none">
                              <pattern id={`lattice-left-${i}`} width="10" height="10" patternUnits="userSpaceOnUse">
                                <path d="M0,5 L5,0 L10,5 L5,10 Z" fill="none" stroke="#4A2512" strokeWidth="0.5"/>
                                <circle cx="5" cy="5" r="1" fill="#5D3A1A"/>
                              </pattern>
                              <rect width="100%" height="100%" fill={`url(#lattice-left-${i})`}/>
                            </svg>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute top-1/2 right-2 md:right-3 -translate-y-1/2">
                      <div className="w-4 h-6 md:w-5 md:h-8 rounded-full border-2 border-[#C97A2B] bg-gradient-to-b from-[#C97A2B] to-[#8B7932]" />
                    </div>
                  </motion.div>
                  
                  {/* Right door */}
                  <motion.div
                    className="w-1/2 h-full origin-right relative"
                    style={{ background: 'linear-gradient(90deg, #3D1F0F 0%, #5D3A1A 20%, #6B3A1F 50%, #5D3A1A 80%, #3D1F0F 100%)', boxShadow: 'inset 3px 0 8px rgba(0,0,0,0.4), -4px 0 12px rgba(0,0,0,0.3)', borderLeft: '3px solid #2D1508', transformStyle: 'preserve-3d' }}
                    initial={{ rotateY: 0 }}
                    animate={isLoaded ? { rotateY: 105 } : { rotateY: 0 }}
                    transition={{ duration: 2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="absolute inset-2 md:inset-3 border-2 border-[#4A2512]">
                      <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full p-1">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="relative border border-[#5D3A1A] overflow-hidden">
                            <svg className="w-full h-full" viewBox="0 0 30 40" preserveAspectRatio="none">
                              <pattern id={`lattice-right-${i}`} width="10" height="10" patternUnits="userSpaceOnUse">
                                <path d="M0,5 L5,0 L10,5 L5,10 Z" fill="none" stroke="#4A2512" strokeWidth="0.5"/>
                                <circle cx="5" cy="5" r="1" fill="#5D3A1A"/>
                              </pattern>
                              <rect width="100%" height="100%" fill={`url(#lattice-right-${i})`}/>
                            </svg>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="absolute top-1/2 left-2 md:left-3 -translate-y-1/2">
                      <div className="w-4 h-6 md:w-5 md:h-8 rounded-full border-2 border-[#C97A2B] bg-gradient-to-b from-[#C97A2B] to-[#8B7932]" />
                    </div>
                  </motion.div>
                </motion.div>
                
                <motion.img
                  src={src} alt={alt} className="w-full h-full object-cover"
                  initial={{ scale: 1.2, filter: 'brightness(0.4)' }}
                  animate={isLoaded ? { scale: 1, filter: 'brightness(1)' } : {}}
                  transition={{ duration: 2.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          </div>

          {/* Right pillar */}
          <div className={`relative ${s.pillar} flex-shrink-0`} style={{ background: 'linear-gradient(90deg, #3D1F0F 0%, #5D3A1A 30%, #6B3A1F 50%, #5D3A1A 70%, #3D1F0F 100%)', boxShadow: '3px 0 10px rgba(0,0,0,0.4)' }}>
            <div className="absolute inset-1 flex flex-col justify-around items-center py-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="relative">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded-full border border-[#8B5A2B]" />
                  <div className="absolute inset-0 flex items-center justify-center"><div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8B5A2B]/40" /></div>
                </div>
              ))}
            </div>
            <div className="absolute -bottom-2 -left-2 w-6 h-8 md:w-8 md:h-12" style={{ background: 'linear-gradient(225deg, #5D3A1A 0%, #3D1F0F 100%)', borderRadius: '0 0 0 100%', clipPath: 'ellipse(100% 100% at 100% 0%)' }} />
          </div>
        </div>

        {/* Bottom Base */}
        <div className="relative mx-4 md:mx-6">
          <div className="relative h-4 md:h-6" style={{ background: 'linear-gradient(180deg, #4A2512 0%, #3D1F0F 100%)' }}>
            <div className="absolute top-0 left-2 right-2 h-2 flex justify-around">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="w-3 h-full bg-[#5D3A1A]" style={{ clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }} />
              ))}
            </div>
          </div>
          <div className="flex justify-center -mt-1">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="w-4 md:w-6 h-3 md:h-4" style={{ background: 'linear-gradient(180deg, #3D1F0F 0%, #2D1508 100%)', borderRadius: '0 0 50% 50%', marginLeft: i > 0 ? '-4px' : '0', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple frame using the real uploaded frame photo
export const NepaleseFrame = ({ src, alt, className = "" }) => {
  return <RealWoodenFrameWithTrigger src={src} alt={alt} className={className} />;
};
